/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import {
  ActivityDetails,
  ActivityRestService,
  Assignee,
  CommunicationListItemDto,
  CommunicationRestService,
  ContactRestService,
  ContactType,
  EmailTemplateDto,
  EmailTemplateRestService,
  GuestListItemDto,
  JobCommunicationRestService,
  LeadRestService,
  MeetingRestService,
  SmsTemplateRestService,
  StageAdvancementResultDto,
  SystemRoleBit,
} from '@gh/builder-shared-data';
import {
  dictionaryItemFromDto,
  DictionaryService,
  globalDictionaries,
  HelpID,
} from '@gh/common-shared';
import { AutocompleteItem, ID } from '@gh/common-shared-data';
import { DialogRouteSnapshot, FormMode, isCreateFormMode, loadTinymce } from '@gh/core-ui';
import { of$ } from '@gh/rx';

import { isNil, omit, sortBy } from 'lodash';
import { Observable } from 'rxjs';

import {
  CommunicationEntryPoint,
  CommunicationListItem,
  CommunicationTarget,
  EmailItem,
  getEmptyMeeting,
  SmsItem,
  WebsiteItem,
} from './communications.model';
import { FilteredDataQuery, getSlicedData } from '@gh/core-data';
import { defaultIfEmpty$, map$, switchMap$ } from '@gh/rx/operators';
import { BuilderUserContext } from '../../user';
import { activityForDoneTrigger, hasActivityId, StageAdvancementNotificationService } from '../../sales';

export type CommunicationgDialogConfig = CommunicationTarget;

export interface EmailDialogConfig extends CommunicationgDialogConfig {
  entryPoint: CommunicationEntryPoint;
  collapseThreshold?: number;
  email?: EmailItem;
  communicationId?: ID;
  gFolderId?: ID;
  mode?: FormMode;
}

export interface SmsDialogConfig extends CommunicationgDialogConfig {
  entryPoint: CommunicationEntryPoint;
  mode?: FormMode;
  sms?: SmsItem;
}

export interface MeetingDialogConfig extends CommunicationgDialogConfig {
  entryPoint: CommunicationEntryPoint;
  meetingId?: ID;
  salesFolderId?: ID;
  contactId?: ID;
  meeting?: ActivityDetails;
  mode: FormMode;
  helpId?: HelpID;
  contactTypeCode?: ContactType;
  leadId?: ID;
}

export interface WebsiteDialogConfig extends CommunicationgDialogConfig {
  entryPoint: CommunicationEntryPoint;
  website: WebsiteItem;
}

export function isCreateEmailDialogConfig(config: EmailDialogConfig): boolean {
  return isNil(config['communicationId']);
}

@Injectable()
export class CommunicationsBusinessService {
  constructor(private communicationRest: CommunicationRestService,
              private smsTemplateRest: SmsTemplateRestService,
              private leadService: LeadRestService,
              private emailTemplateRest: EmailTemplateRestService,
              private leadMeetingRest: MeetingRestService,
              private userContext: BuilderUserContext,
              private dictionaryService: DictionaryService,
              private contactRest: ContactRestService,
              private activityService: ActivityRestService,
              private jobCommunicationRest: JobCommunicationRestService,
              private stageAdvancementNotifications: StageAdvancementNotificationService) {
  }

  initEmailDialogView(route: DialogRouteSnapshot<EmailDialogConfig>): any {
    const {input} = route;

    if (isCreateEmailDialogConfig(<EmailDialogConfig>{...input.email})) {
      return {
        email: input.email,
        tinymce: loadTinymce(),
      };
    } else {
      return {
        email: this.communicationRest.get(input.communicationId!),
      };
    }
  }

  initMeetingDialogView(route: DialogRouteSnapshot<MeetingDialogConfig>): any {
    const isMasterOrCorp = this.userContext.hasSystemRole(SystemRoleBit.Master) || this.userContext.hasSystemRole(SystemRoleBit.Corporate);
    return {
      assignee: isMasterOrCorp ? null : this.activityService.getCurrentAssignee(),
      activityTypes: this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.activityType)
        .pipe((map$((items) => items.map(dictionaryItemFromDto)))),
      salesFolderList: (route.input.entryPoint === CommunicationEntryPoint.LeadList
        || route.input.entryPoint === CommunicationEntryPoint.LeadCommunication) && route.input.leadId ?
        this.leadService.get(route.input.leadId).pipe(map$(
          (lead) => sortBy(lead.salesFolders, 'created'))) : [],
      meeting: isCreateFormMode(route.input.mode) ? getEmptyMeeting() : this.getMeeting(route.input, route.input.meetingId!),
      };
  }

  sendSms(entryPoint: CommunicationEntryPoint,
          target: CommunicationTarget,
          sms: SmsItem): Observable<CommunicationListItem> {
    switch (entryPoint) {
      case CommunicationEntryPoint.Job:
        return this.jobCommunicationRest.sendSms(target.jobId!, sms);
      case CommunicationEntryPoint.Contact:
        return this.communicationRest.sendContactSms(sms);
      case CommunicationEntryPoint.Lead:
        return this.communicationRest.sendLeadSms(sms);
      default:
        throw new Error(`CommunicationBusinessService: entry point '${entryPoint}' is not specified or unknown`);
    }
  }

  getContacts(
    query: FilteredDataQuery<string>,
    emailType: string,
    types: ContactType[] = [ContactType.Employee, ContactType.CreditorSupplier, ContactType.CityCounty, ContactType.Customer, ContactType.Debtor, ContactType.FinanceCompany, ContactType.Other],
  ): Observable<AutocompleteItem[]> {
    return this.contactRest.findAllAsDataSource({
      types,
      autocomplete: true,
      showInactive: false,
      shouldHaveEmail: true,
      emailType,
      name: query.filter,
    }).select(query).pipe(
      map$(getSlicedData));
  }

  readSms(id: ID): Observable<CommunicationListItem> {
    return this.communicationRest.setSmsAsRead(id);
  }

  sendEmail(entryPoint: CommunicationEntryPoint,
            target: CommunicationTarget,
            email: EmailItem): Observable<CommunicationListItem> {
    switch (entryPoint) {
      case CommunicationEntryPoint.Job: {
        const jobId = target.jobId || email.jobId;

        return this.jobCommunicationRest.sendEmail(jobId!, email);
      }
      case CommunicationEntryPoint.Contact:
        return this.communicationRest.sendContactEmail(email);
      case CommunicationEntryPoint.Lead:
        return this.communicationRest.sendLeadEmail(email);
      default:
        throw new Error(`CommunicationBusinessService: entry point '${entryPoint}' is not specified or unknown`);
    }
  }

  getSmsTemplates(): Observable<AutocompleteItem[]> {
    return this.smsTemplateRest.getSmsTemplates();
  }

  getFilledSmsTemplate(id: string, contactId?: string, jobId?: string): Observable<any> {
    return this.smsTemplateRest.getTemplate(id, contactId ? contactId : null, jobId ? jobId : null);
  }

  getEmailTemplates(): Observable<EmailTemplateDto[]> {
    return this.emailTemplateRest.getEmailTemplates();
  }

  getFilledEmailTemplate(id: string, contactId?: string, jobId?: string): Observable<any> {
    return this.emailTemplateRest.getTemplate(id, contactId ? contactId : null, jobId ? jobId : null);
  }

  getMeeting(input: MeetingDialogConfig,
             meetingId: ID): Observable<ActivityDetails> {
    switch (input.entryPoint) {
      case CommunicationEntryPoint.Job:
        return this.activityService.getJobActivity(input.jobId!, meetingId);
      case CommunicationEntryPoint.Lead:
        return of$(input.meeting!);
      case CommunicationEntryPoint.LeadBord:
        if (input.meeting) {
          return  of$(input.meeting);
        } else {
          return this.activityService.get(input.salesFolderId!, meetingId);
        }
      case CommunicationEntryPoint.LeadCommunication:
        return this.activityService.get(input.salesFolderId!, meetingId);
      case CommunicationEntryPoint.LeadList:
        return this.activityService.get(input.salesFolderId!, meetingId);
      case CommunicationEntryPoint.Contact:
        return this.activityService.getContactActivity(input.contactId!, meetingId);
      default:
        throw new Error(`CommunicationBusinessService: entry point '${input.entryPoint}' is not specified or unknown`);
    }
  }

  saveMeeting(entryPoint: CommunicationEntryPoint,
              target: CommunicationTarget,
              meeting: ActivityDetails): Observable<ActivityDetails | ActivityDetails[] | CommunicationListItemDto> {
    const wasDone = hasActivityId(meeting.id) ? !!(target as MeetingDialogConfig).meeting?.isDone : false;
    const save$ = hasActivityId(meeting.id)
      ? this.updateMeeting(entryPoint, target, meeting)
      : this.createMeeting(entryPoint, target, meeting);
    return save$.pipe(switchMap$((saved) =>
      this.notifyLeadActivityDone(
        entryPoint,
        target.salesFolderId,
        activityForDoneTrigger(meeting, saved),
        wasDone,
      ).pipe(
        defaultIfEmpty$(undefined),
        map$(() => saved),
      )));
  }

  createMeeting(entryPoint: CommunicationEntryPoint,
                target: CommunicationTarget,
                meeting: ActivityDetails): Observable<ActivityDetails | ActivityDetails[] | CommunicationListItemDto> {
    switch (entryPoint) {
      case CommunicationEntryPoint.Job: {
        const jobId = target?.jobId;
        if (jobId == null) {
          throw new Error('Job entry point requires target.jobId');
        }
        return this.activityService.createJobActivity(jobId, {...omit(meeting, 'id')});
      }
      case CommunicationEntryPoint.Lead:
        return this.activityService.create(target.salesFolderId!, meeting);
      case CommunicationEntryPoint.LeadBord:
        return this.activityService.create(target.salesFolderId!, meeting);
      case CommunicationEntryPoint.LeadList:
        return this.activityService.create(target.salesFolderId!, meeting);
      case CommunicationEntryPoint.LeadCommunication:
        return this.activityService.leadCommunicationCreate(target.salesFolderId!, meeting);
      case CommunicationEntryPoint.Contact:
        return this.activityService.createContactActivity(target.contactId || '', {...omit(meeting, 'id')});
      default:
        throw new Error(`CommunicationBusinessService: entry point '${entryPoint}' is not specified or unknown`);
    }
  }

  updateMeeting(entryPoint: CommunicationEntryPoint,
                target: CommunicationTarget,
                meeting: ActivityDetails): Observable<ActivityDetails | ActivityDetails[] | CommunicationListItemDto> {
    switch (entryPoint) {
      case CommunicationEntryPoint.Job:
        return this.activityService.updateJobActivity(target.jobId!, meeting);
      case CommunicationEntryPoint.Lead:
        return this.activityService.update(target.salesFolderId!, meeting);
      case CommunicationEntryPoint.LeadBord:
        return this.activityService.update(target.salesFolderId!, meeting);
      case CommunicationEntryPoint.LeadList:
        return this.activityService.update(target.salesFolderId!, meeting);
      case CommunicationEntryPoint.LeadCommunication:
        return this.activityService.leadCommunicationUpdate(target.salesFolderId!, meeting);
      case CommunicationEntryPoint.Contact:
        return this.activityService.updateContactActivity(target.contactId || '', meeting);
      default:
        throw new Error(`CommunicationBusinessService: entry point '${entryPoint}' is not specified or unknown`);
    }
  }

  markAsRead(filter: any): Observable<any> {
    return this.communicationRest.markMessagesReadOrUnread(filter);
  }

  onReply(sms: any): Observable<any> {
    return this.communicationRest.replySms(sms);
  }

  findGuestsByName(sfId: ID, query: FilteredDataQuery<string>, contactId?: ID): Observable<GuestListItemDto[]> {
    return this.activityService.findGuests(sfId, {
                                                           name: query.filter || '',
                                                         }, contactId).select(query).pipe(map$(getSlicedData));
  }
  findAssigneeByName(query: FilteredDataQuery<string>): Observable<Assignee[]> {
    return this.activityService.findAssignee({
      name: query.filter || '',
    }).select(query).pipe(map$(getSlicedData));
  }

  private notifyLeadActivityDone(entryPoint: CommunicationEntryPoint,
                                 salesFolderId: ID | undefined,
                                 meeting: ActivityDetails,
                                 wasDone: boolean): Observable<StageAdvancementResultDto | undefined> {
    if (entryPoint === CommunicationEntryPoint.Job || entryPoint === CommunicationEntryPoint.Contact) {
      return of$(undefined);
    }
    return this.stageAdvancementNotifications.notifyAfterActivityMarkedDone(salesFolderId, meeting, wasDone);
  }
}
